const regexYoutubeLink =
  /^((?:https?:)?\/\/)?((?:www)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/|shorts\/)?)([\w-]+)(\S+)?$/gm

export function isYoutubeLink(url: string) {
  return !!url.match(regexYoutubeLink)
}

export function convertYoutubeLinkToString(url: string) {
  return isYoutubeLink(url) ? "youtube" : url
}

export function convertYoutubeLinkToEmbed(url: string) {
  const match = isYoutubeLink(url)
  const matches = regexYoutubeLink.exec(url)
  if (match && matches?.[5]) {
    const videoId = matches[5]
    return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&autoplay=0&controls=0`
  }
  return url
}

export async function checkVimeoVideoExists(videoId: string): Promise<boolean> {
  const res = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`)
  return res.ok
}

/**
 * Check that a YouTube video actually exists (and is publicly embeddable).
 * Uses YouTube's oEmbed endpoint, which returns 200 only when the video is
 * available and 401/404 otherwise. No API key required.
 * @param url - The URL of the YouTube video
 * @returns Whether the video exists
 */
export async function checkYoutubeVideoExists(url: string): Promise<boolean> {
  try {
    const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
    return res.ok
  } catch (error) {
    console.error("Error checking YouTube video:", error)
    return false
  }
}

export function extractVimeoId(link: string): string | null {
  const match = /vimeo\.com\/(\d+)/.exec(link)
  return match?.[1] ? match[1] : null
}

export function isVimeoLink(url: string) {
  return /^(https?:)?\/\/((((player|www)\.)?vimeo\.com)|((player\.)?[a-zA-Z0-9-]+\.(videoji\.(hk|cn)|vimeo\.work)))(?=$|\/)/.test(
    url,
  )
}

/**
 * Get the thumbnail URL of a YouTube video
 * @param url - The URL of the YouTube video
 * @returns The thumbnail URL of the YouTube video
 */

// Cache for YouTube thumbnails
const youtubeThumbnailCache = new Map<string, string>()

export function getYouTubeThumbnailUrl(
  url: string,
  quality: "maxresdefault" | "hqdefault" | "mqdefault" = "mqdefault",
): string | null {
  if (youtubeThumbnailCache.has(url)) {
    return youtubeThumbnailCache.get(url) || null
  }
  const matches = regexYoutubeLink.exec(url)
  if (matches?.[5]) {
    const videoId = matches[5]
    // maxresdefault.jpg = high resolution (1280x720)
    // hqdefault.jpg = standard quality (480x360)
    // mqdefault.jpg = medium quality (320x180)
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/${quality}.jpg`
    youtubeThumbnailCache.set(url, thumbnailUrl)
    return thumbnailUrl
  }
  return null
}

/**
 * Get the thumbnail URL of a Vimeo video
 * @param url - The URL of the Vimeo video
 * @returns The thumbnail URL of the Vimeo video
 */
// Cache for Vimeo thumbnails
const vimeoThumbnailCache = new Map<string, string>()

export async function getVimeoThumbnailUrl(url: string): Promise<string | null> {
  // Check the cache first
  if (vimeoThumbnailCache.has(url)) {
    return vimeoThumbnailCache.get(url) || null
  }

  const videoId = extractVimeoId(url)
  if (!videoId) return null

  try {
    const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`)
    if (!response.ok) return null

    const data = (await response.json()) as { thumbnail_url?: string }
    const thumbnailUrl = data.thumbnail_url || null

    // Cache the thumbnail
    if (thumbnailUrl) {
      vimeoThumbnailCache.set(url, thumbnailUrl)
    }
    return thumbnailUrl
  } catch (error) {
    console.error("Error fetching Vimeo thumbnail:", error)
    return null
  }
}
